/**
 * "Board by status" as a rail (docs/ux/pipeline.md): sixteen cells, All and
 * 1 to 15, each with its number chip, count and overdue badge, under a row
 * of saved-view labels that press the cells they span. Parking states 13 to
 * 15 sit after a rule with square chips. One roving tab stop; arrows move
 * between cells. Under 1024 px the rail scrolls horizontally and the label
 * row hides (the views live in the filter sheet or the View select).
 */

import { ALL_STATUSES, statusLabel, type Status } from '@beam/core/status';
import { cn } from 'cn';
import { useRef, useState, type KeyboardEvent } from 'react';
import type { PipelineSummary } from '@/app/types';
import { StatusChip } from '@/components/matter/StatusChip';
import { Skeleton } from '@/components/ui/skeleton';
import { PIPELINE_VIEWS, type ViewId } from './views';

export type StatusStripProps = {
  counts: PipelineSummary['byStatus'] | null;
  total: number | null;
  overdueTotal: number | null;
  loading: boolean;
  statuses: Set<Status>;
  view: ViewId | null;
  onToggleStatus: (status: Status) => void;
  onToggleView: (view: ViewId) => void;
  onClear: () => void;
};

/** Grid columns: All, 1 to 12, a 12 px rule track, 13 to 15. */
const GRID = 'lg:grid-cols-[56px_repeat(12,minmax(0,1fr))_12px_repeat(3,minmax(0,1fr))]';

function viewColumn(view: (typeof PIPELINE_VIEWS)[number]): string {
  const first = view.statuses[0];
  const last = view.statuses[view.statuses.length - 1]!;
  const col = (s: Status) => (s >= 13 ? s + 2 : s + 1);
  return `${col(first)} / ${col(last) + 1}`;
}

export function StatusStrip({ counts, total, overdueTotal, loading, statuses, view, onToggleStatus, onToggleView, onClear }: StatusStripProps) {
  const group = useRef<HTMLDivElement>(null);
  const [focusIndex, setFocusIndex] = useState(0);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const cells = Array.from(group.current?.querySelectorAll<HTMLButtonElement>('[data-strip-cell]') ?? []);
    if (cells.length === 0) return;
    const current = cells.findIndex((c) => c === document.activeElement);
    let next = current;
    if (e.key === 'ArrowRight') next = Math.min(cells.length - 1, current + 1);
    else if (e.key === 'ArrowLeft') next = Math.max(0, current - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = cells.length - 1;
    else return;
    e.preventDefault();
    setFocusIndex(next);
    cells[next]?.focus();
    cells[next]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  const allPressed = statuses.size === 0;
  const cellBase = 'flex h-16 min-w-[96px] flex-col justify-between rounded-[6px] border border-beam-line bg-beam-paper px-1.5 py-1.5 text-left transition-colors duration-[140ms] ease-beam hover:bg-beam-cream-deep lg:min-w-0';
  const pressedClass = 'bg-beam-cream-deep shadow-[inset_0_-2px_0_0_var(--beam-signature)]';

  return (
    <div data-slot="status-strip" className="flex flex-col gap-1">
      {/* Two lines of 11 px sentence case: the uppercase letterspaced form truncated
          "Paperwork outstanding" at every desktop width (audit finding, P1). */}
      <div className={cn('hidden lg:grid', GRID, 'h-[26px] items-end gap-1')} aria-hidden={false}>
        {PIPELINE_VIEWS.map((v) => {
          const pressed = view === v.id;
          const n = counts ? v.statuses.reduce((sum, s) => sum + (counts[String(s)]?.count ?? 0), 0) : null;
          return (
            <button
              key={v.id}
              type="button"
              aria-pressed={pressed}
              aria-label={`${v.label}: ${v.statuses.length === 1 ? `${v.statuses[0]} ${statusLabel(v.statuses[0])}` : `${v.statuses[0]} ${statusLabel(v.statuses[0])} to ${v.statuses.at(-1)} ${statusLabel(v.statuses.at(-1)!)}`}${n !== null ? `, ${n} matters` : ''}`}
              onClick={() => onToggleView(v.id)}
              style={{ gridColumn: viewColumn(v) }}
              className={cn('line-clamp-2 rounded-sm px-1 text-left text-[11px] leading-[1.15] font-medium text-beam-text-soft hover:text-foreground', pressed && 'text-beam-signature underline decoration-2 underline-offset-2')}
            >
              {v.label}
            </button>
          );
        })}
      </div>
      {/* A block-level scroller around the flex row: WebKit lets a flex container's
          overflowing items widen the document even when the container clips. */}
      <div data-slot="status-strip-scroller" className="relative overflow-x-auto pb-1 [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] lg:overflow-visible lg:pb-0 lg:[mask-image:none]">
      <div
        ref={group}
        role="group"
        aria-label="Filter by status"
        onKeyDown={onKeyDown}
        className={cn('flex w-max gap-1 lg:grid lg:w-auto', GRID)}
      >
        <button
          type="button"
          data-strip-cell
          data-status="all"
          tabIndex={focusIndex === 0 ? 0 : -1}
          onFocus={() => setFocusIndex(0)}
          aria-pressed={allPressed}
          aria-label={total !== null ? `All statuses, ${total} cases${overdueTotal ? `, ${overdueTotal} overdue` : ''}` : 'All statuses'}
          onClick={onClear}
          className={cn(cellBase, 'lg:min-w-[56px]', allPressed && pressedClass)}
        >
          <span className="flex items-center gap-1 text-[13px] leading-none">
            <span className="font-medium">All</span>
            {loading ? <Skeleton className="h-3 w-5" /> : <span className="tabular ml-auto font-semibold">{total ?? ' '}</span>}
          </span>
          {/* The All column is 56px on the grid, which leaves about 44px of content: "matters" and the
              badge together need half as much again and overflowed into the next cell. The badge wins
              the row when there is one, because the number is the information; the accessible name
              above still reads "All statuses, 30 cases, 11 overdue". */}
          <span className="flex items-center justify-between text-[11px] leading-tight text-beam-text-soft">
            {overdueTotal ? <OverdueBadge n={overdueTotal} /> : <span>cases</span>}
          </span>
        </button>
        {ALL_STATUSES.map((s, i) => {
          const pressed = statuses.has(s);
          const cell = counts?.[String(s)];
          const index = i + 1;
          return (
            <button
              key={s}
              type="button"
              data-strip-cell
              data-status={s}
              data-shape={s >= 13 ? 'square' : 'pill'}
              tabIndex={focusIndex === index ? 0 : -1}
              onFocus={() => setFocusIndex(index)}
              aria-pressed={pressed}
              aria-label={`${s} ${statusLabel(s)}${cell ? `, ${cell.count} ${cell.count === 1 ? 'case' : 'cases'}${cell.overdue ? `, ${cell.overdue} overdue` : ''}` : ''}`}
              onClick={() => onToggleStatus(s)}
              style={s === 13 ? { gridColumnStart: 15 } : undefined}
              className={cn(cellBase, s === 13 && 'lg:relative lg:before:absolute lg:before:-left-[8px] lg:before:top-2 lg:before:bottom-2 lg:before:w-px lg:before:bg-beam-line-strong', pressed && pressedClass)}
            >
              <span className="flex items-center gap-1 leading-none">
                <StatusChip status={s} variant="number" size="sm" />
                {loading ? <Skeleton className="h-3 w-4" /> : <span className={cn('tabular text-[13px] font-semibold', cell?.count === 0 && 'text-beam-text-faint')}>{cell?.count ?? ' '}</span>}
                {cell?.overdue ? <OverdueBadge n={cell.overdue} className="ml-auto" /> : null}
              </span>
              <span className="line-clamp-2 text-[11px] leading-[1.15] text-beam-text-soft">{statusLabel(s)}</span>
            </button>
          );
        })}
      </div>
      </div>
    </div>
  );
}

function OverdueBadge({ n, className }: { n: number; className?: string }) {
  return (
    <span aria-label={`${n} overdue`} className={cn('tabular inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-beam-error-bg px-1 text-[11px] font-semibold text-beam-error', className)}>
      {n}
    </span>
  );
}
